import React from 'react';
import { View, Text, StyleSheet, ScrollView } from 'react-native';
import { RouteProp, useRoute } from '@react-navigation/native';
import { useSelector } from 'react-redux';

import { RootState } from '@/store';
import { ClassesStackParamList } from '@/types/navigation';
import RotationRows from '@/components/classes/RotationRows';
import { useChicagoSeconds } from '@/hooks/useChicagoSeconds';
import { chicagoToday } from '@/utils/scheduleWeek';
import { GROUP_LABELS, formatClockTime, formatDayLabel, studentBadge } from '@/utils/rotations';
import { colors, font, space, radius } from '@/theme/chalkCourt';

const LOCATION_NAMES: Record<string, string> = { SL: 'Southlake', RM: 'Rhome' };

/**
 * One class: its whole day of rotations, coloured live on today (kiosk
 * rules), then its roster ("First L."; makeups / trials / one-day attendees
 * badged as on the kiosk). Reads the day already held in the
 * myClasses (or, for an admin's staff view, staffClasses) slice — no extra request.
 */
const ClassDetailScreen: React.FC = () => {
  const { params } = useRoute<RouteProp<ClassesStackParamList, 'ClassDetail'>>();
  // "staff" = an admin looking at someone else's day (held in the staffClasses slice).
  const data = useSelector((state: RootState) =>
    params.source === 'staff' ? state.staffClasses.member : state.myClasses.data
  );
  const cls = data?.classes.find((c) => c.key === params.classKey);
  // Live colours only while the held day is still the device's Chicago today.
  const heldIsToday = !!data && data.date === data.today;
  const nowSec = useChicagoSeconds(heldIsToday);
  const isToday = heldIsToday && data!.today === chicagoToday();

  if (!data || !cls) {
    return (
      <View style={styles.screen}>
        <Text style={styles.missing}>This class is no longer on the selected day.</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.screen} contentContainerStyle={styles.content}>
      <Text style={styles.kicker}>
        {formatDayLabel(data.date).toUpperCase()} · {GROUP_LABELS[cls.group].toUpperCase()}
      </Text>
      <Text style={styles.time}>
        {formatClockTime(cls.start)} – {formatClockTime(cls.end)}
        {cls.location ? ` · ${LOCATION_NAMES[cls.location]}` : ''}
      </Text>
      {cls.subbingFor && <Text style={styles.sub}>Subbing for {cls.subbingFor}</Text>}
      {cls.coCoaches.length > 0 && (
        <Text style={styles.coaches}>With {cls.coCoaches.join(', ')}</Text>
      )}

      <Text style={styles.sectionTitle}>ROTATIONS</Text>
      {cls.rotations.length > 0 ? (
        <View style={styles.panel}>
          <RotationRows rotations={cls.rotations} nowSec={isToday ? nowSec : null} />
        </View>
      ) : (
        <Text style={styles.empty}>
          {cls.code === null
            ? 'This shift isn’t linked to a class section, so there are no rotations.'
            : 'No rotations are set up for this class in the active schedule.'}
        </Text>
      )}

      <Text style={styles.sectionTitle}>STUDENTS · {cls.students.length}</Text>
      {cls.students.length > 0 ? (
        <View style={[styles.panel, styles.students]}>
          {cls.students.map((s, i) => {
            const badge = studentBadge(s);
            return (
              <Text key={`${s.display}-${i}`} style={styles.student}>
                {s.display}
                {badge && <Text style={styles.badge}>{`  ${badge}`}</Text>}
              </Text>
            );
          })}
        </View>
      ) : (
        <Text style={styles.empty}>No students enrolled on this day.</Text>
      )}
      {/* Extras are a today-only snapshot; say so rather than imply there are none. */}
      {isToday && cls.code !== null && !cls.makeupsIncluded && (
        <Text style={[styles.empty, styles.note]}>Makeups not shown yet.</Text>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: colors.bg },
  content: { padding: space.lg, paddingBottom: space.xxl },
  missing: { fontFamily: font.body, fontSize: 15, color: colors.inkSoft, padding: space.lg },
  kicker: { fontFamily: font.bodyBold, fontSize: 13, letterSpacing: 2, color: colors.accent },
  time: { fontFamily: font.displayBold, fontSize: 24, color: colors.ink, marginTop: 2 },
  sub: { fontFamily: font.bodySemi, fontSize: 14, color: colors.warn, marginTop: space.xs },
  coaches: { fontFamily: font.body, fontSize: 14, color: colors.inkSoft, marginTop: space.xs },
  sectionTitle: {
    fontFamily: font.bodyBold,
    fontSize: 13,
    letterSpacing: 2,
    color: colors.inkSoft,
    marginTop: space.xl,
    marginBottom: space.sm,
  },
  panel: { backgroundColor: colors.surface, borderRadius: radius.md, padding: space.sm },
  students: { flexDirection: 'row', flexWrap: 'wrap' },
  student: {
    width: '50%',
    fontFamily: font.bodyMed,
    fontSize: 16,
    color: colors.ink,
    paddingVertical: space.xs,
    paddingHorizontal: space.sm,
  },
  badge: { fontFamily: font.bodyBold, fontSize: 12, letterSpacing: 1, color: colors.accent },
  empty: { fontFamily: font.body, fontSize: 14, color: colors.inkSoft },
  note: { marginTop: space.sm },
});

export default ClassDetailScreen;
